import React, { useEffect, useState } from "react";
import WhiteTitledCard from "../../../components/cards/WhiteTitledCard";
import FilterByButton from "../../ui/FilterButton";
import { useAppSelector, useAppDispatch } from "../../../redux/app/hooks";
import { RootState } from "../../../redux/app/store";
import ProductSummary from "./ProductSummary";
import ProductsTable from "./ProductTable";
import DateRangeSelector, { DateRangeType } from "../../ui/DateRangeSelector";
import Button from "../../ui/Button";
import SearchInput from "../../../components/form/SearchInput";
import {
  fetchCategoriesAsync,
  updateFilters,
} from "../../../redux/features/ProductSlice";
import {
  fetchUsersAsync,
  resetAdminState,
} from "../../../redux/features/adminSlice";
import { mapDataToLabelValue } from "../../../helpers";
import Dropdown from "../../ui/SelectDropdown";

const ProductManagement = () => {
  const [inputValue, setInputValue] = useState("");
  const [selectedRange, setSelectedRange] = useState<DateRangeType>({
    startDate: null,
    endDate: null,
  });

  const [vendorSearch, setVendorSearch] = useState("");

  // ✅ pull vendors directly from Redux
  const { vendors } = useAppSelector((state: RootState) => state.admin);
  const {
    pendingProducts,
    rejectedProducts,
    allProducts,
    approvedProducts,
    productStats,
    categories,
    vendorId,
    status,
    categoryId,
  } = useAppSelector((state: RootState) => state.products);

  const dispatch = useAppDispatch();

  // Map Redux vendors to dropdown options
  const vendorOptions =
    [{ label: "All", value: "ALL" }].concat(
      vendors?.data?.data?.map((v: any) => ({
        label: v.fullName,
        value: v.id,
      }))
    ) ?? [];

  const handleDateChange = (range: DateRangeType) => {
    setSelectedRange(range);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      dispatch(updateFilters({ search: inputValue }));
    }
  };

  const tabList = [
    {
      id: 1,
      name: "All",
      value: "ALL",
      count: allProducts?.meta?.totalItems ?? productStats?.totalProducts ?? 0,
    },
    {
      id: 2,
      name: "Pending",
      value: "PENDING",
      count:
        pendingProducts?.meta?.totalItems ?? productStats?.pendingProducts ?? 0,
    },
    {
      id: 3,
      name: "Approved",
      value: "APPROVED",
      count:
        approvedProducts?.meta?.totalItems ??
        productStats?.approvedProducts ??
        0,
    },
    {
      id: 4,
      name: "Rejected",
      value: "REJECTED",
      count:
        rejectedProducts?.meta?.totalItems ??
        productStats?.rejectedProducts ??
        0,
    },
  ];

  // Fetch categories once
  useEffect(() => {
    dispatch(fetchCategoriesAsync());
  }, [dispatch]);

  // ✅ Fetch vendors initially
  useEffect(() => {
    dispatch(resetAdminState());
    dispatch(
      fetchUsersAsync({
        page: 1,
        limit: 20,
        filter: "",
        userType: "VENDOR",
      })
    );
  }, [dispatch]);

  // ✅ Fetch vendors when searching
  useEffect(() => {
    if (vendorSearch === "" && vendors?.data?.data?.length) return; // skip if already loaded and no search
    const timeout = setTimeout(() => {
      dispatch(
        fetchUsersAsync({
          page: 1,
          limit: 20,
          filter: vendorSearch,
          userType: "VENDOR",
        })
      );
    }, 400);
    return () => clearTimeout(timeout);
  }, [vendorSearch, dispatch]);

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6 md:p-8 bg-gray-100 min-h-screen">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold mb-2">Product Management</h1>
      </div>

      {/* Summary Cards */}
      <ProductSummary />

      <WhiteTitledCard>
        <div className="px-6">
          {/* Tabs with counts */}
          <div className="overflow-x-auto mb-6">
            <div className="flex gap-6 border-b min-w-max px-1">
              {tabList.map((tab) => (
                <div
                  key={tab.id}
                  onClick={() => {
                    dispatch(
                      updateFilters({ status: tab.value, productPage: 0 })
                    );
                  }}
                  className={`pb-2 cursor-pointer text-sm font-semibold flex items-center gap-2 whitespace-nowrap ${
                    status === tab.value
                      ? "text-primary border-b-2 border-primary"
                      : "text-gray-500"
                  }`}
                >
                  <span>{tab.name}</span>
                  <span className="bg-lightPurple px-2 text-accent rounded-md">
                    {tab.count}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Unified Filter Bar */}
          <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between mb-6 align-baseline">
            <div className="flex flex-wrap gap-4 items-center align-baseline">
              <DateRangeSelector
                initialRange={selectedRange}
                onChange={handleDateChange}
              />
              <div style={{ width: "250px" }}>
                <Dropdown
                  placeholder="Search by vendor name"
                  options={vendorOptions}
                  value={vendorId}
                  onSelect={(val: any) => {
                    dispatch(updateFilters({ vendorId: val }));
                  }}
                  onSearch={(query) => setVendorSearch(query)}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-4 items-center">
              {/* Category Filter */}
              <FilterByButton
                options={[
                  { label: "All", value: null },
                  ...mapDataToLabelValue(categories ?? [], "title", "id"),
                ]}
                onSelect={(value) =>
                  dispatch(updateFilters({ categoryId: value }))
                }
                buttonLabel="Filter By Category"
                defaultValue={categoryId}
              />

              {/* ✅ Vendor Filter with Redux vendors */}

              <div className="flex items-center gap-2">
                <SearchInput
                  placeholder="Search product name"
                  onChange={(e: any) => setInputValue(e.target.value)}
                  value={inputValue}
                  onKeyDown={handleKeyDown}
                />
                <Button
                  layout="toolbar" size="sm"
                  label="Search"
                  onClick={() =>
                    dispatch(updateFilters({ search: inputValue }))
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <ProductsTable />
      </WhiteTitledCard>
    </div>
  );
};

export default ProductManagement;
